import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native'
import { clsx } from 'clsx'
import { Image } from 'expo-image'
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import { AccessibilityInfo, View, type LayoutChangeEvent } from 'react-native'
import Carousel, { type ICarouselInstance } from 'react-native-reanimated-carousel'

import placeholder from '@/assets/images/placeholder.png'
import { Box, Button, ButtonIcon, HStack, type HStackProps } from '@/components'
import { COLOR_WHITE } from '@/lib'

import { PhotoCarouselCaption } from './PhotoCarouselCaption'
import { type MinimalPhoto } from './types'

export interface PhotoCarouselProps extends HStackProps {
  data: MinimalPhoto[]
  withDescriptions?: boolean
  withPlaceholder?: boolean
  withRoundCorners?: boolean
}

export const PhotoCarousel = forwardRef<View, PhotoCarouselProps>(
  ({ children, data, onLayout, withDescriptions = true, withPlaceholder = true, withRoundCorners, ...props }, ref) => {
    const carouselRef = useRef<ICarouselInstance>(null)
    const hasMultiplePhotos = data.length > 1
    const onNextPress = useCallback(() => carouselRef.current?.next(), [])
    const onPrevPress = useCallback(() => carouselRef.current?.prev(), [])
    const [photoIndex, setPhotoIndex] = useState(0)
    const [photoSize, setPhotoSize] = useState(512)
    const getPhotoAccessibilityLabel = useCallback(
      (index: number) => {
        if (!data.length) {
          return 'No photos to display'
        }

        const photo = data[index]

        return `Photo ${index + 1} of ${data.length}, ${photo?.description ? `Caption: ${photo?.description}` : 'No caption'}`
      },
      [data]
    )

    return (
      <HStack
        className={clsx(
          'items-center justify-center',
          withRoundCorners && 'overflow-hidden rounded-lg border border-brand-grey-100'
        )}
        onLayout={useCallback(
          (event: LayoutChangeEvent) => {
            if (event.nativeEvent.layout.width) {
              setPhotoSize(event.nativeEvent.layout.width)
            }
            onLayout?.(event)
          },
          [onLayout]
        )}
        {...props}
      >
        <Box
          accessible
          accessibilityHint={
            hasMultiplePhotos ? 'Use the next and previous buttons to switch between photos' : undefined
          }
          accessibilityLabel={`Photo Carousel. ${getPhotoAccessibilityLabel(photoIndex)}`}
          accessibilityRole='image'
          focusable
          importantForAccessibility='yes'
          ref={ref}
          tabIndex={0}
        >
          <Box
            accessible={false}
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility='no-hide-descendants'
          >
            <Carousel
              autoFillData={hasMultiplePhotos}
              data={useMemo<MinimalPhoto[]>(
                () =>
                  !data.length && withPlaceholder
                    ? [
                        {
                          blurhash: null,
                          description: null,
                          source: null
                        }
                      ]
                    : data,
                [data, withPlaceholder]
              )}
              enabled={hasMultiplePhotos}
              loop={hasMultiplePhotos}
              onSnapToItem={useCallback(
                (index: number) => {
                  setPhotoIndex(index)

                  if (typeof AccessibilityInfo.announceForAccessibility === 'function') {
                    const photoAccessibilityLabel = getPhotoAccessibilityLabel(index)

                    if (photoAccessibilityLabel) {
                      AccessibilityInfo.announceForAccessibility(photoAccessibilityLabel)
                    }
                  }
                },
                [getPhotoAccessibilityLabel]
              )}
              ref={carouselRef}
              renderItem={useCallback(
                ({ item }: { item: MinimalPhoto }) => (
                  <Box className='relative'>
                    <Image
                      cachePolicy='memory-disk'
                      className='pointer-events-none aspect-square w-full'
                      contentFit='cover'
                      contentPosition='center'
                      placeholder={item?.blurhash ? { blurhash: item.blurhash } : placeholder}
                      placeholderContentFit='cover'
                      source={item?.source ?? placeholder}
                    />

                    {withDescriptions && item?.description ? (
                      <PhotoCarouselCaption
                        description={item.description}
                        width={photoSize}
                      />
                    ) : null}
                  </Box>
                ),
                [photoSize, withDescriptions]
              )}
              height={photoSize}
              width={photoSize}
            />
          </Box>
        </Box>
        {hasMultiplePhotos ? (
          <Button
            accessibilityLabel='Next Photo'
            action='secondary'
            className='absolute right-2 top-1/2 h-10 w-10 cursor-pointer rounded-full bg-brand-black-400 p-0 opacity-80'
            onPress={onNextPress}
          >
            <ButtonIcon
              as={IconChevronRight}
              className='h-1/2 w-1/2'
              color={COLOR_WHITE}
              strokeWidth={2}
            />
          </Button>
        ) : null}
        {hasMultiplePhotos ? (
          <Button
            accessibilityLabel='Previous Photo'
            action='secondary'
            className='absolute left-2 top-1/2 h-10 w-10 rounded-full bg-brand-black-400 p-0 opacity-80'
            onPress={onPrevPress}
          >
            <ButtonIcon
              as={IconChevronLeft}
              className='h-1/2 w-1/2'
              color={COLOR_WHITE}
              strokeWidth={2}
            />
          </Button>
        ) : null}
        {children}
      </HStack>
    )
  }
)

PhotoCarousel.displayName = 'PhotoCarousel'
